import { useMutation } from "@tanstack/react-query";
import { useRef } from "react";
import { z } from "zod";

import { useNotifications } from "~/shared/hooks/use-notifications.ts";
import { api } from "~/shared/services/api/fetch-client.ts";

import { mapUpdateShipmentDetailsErrorToUserMessage } from "./use-update-shipment-details-error-mapper.ts";

const updateShipmentDetailsErrorSchema = z.array(
	z.object({
		code: z.string(),
		message: z.string(),
	}),
);

export type StopDetailsCommand = {
	id: string;
	stopType: "Pickup" | "Delivery" | undefined;
	locationName?: string;
	streetAddress?: string;
	pickupNumber?: string;
	appointmentInformation?: string;
};

export type UpdateShipmentDetailsCommand = {
	stops: StopDetailsCommand[];
	customerPO?: string;
	trailerSize?: string;
	specialInstructions?: string;
	quoteId: number;
};

async function callUpdateShipmentDetails(
	request: UpdateShipmentDetailsCommand,
): Promise<void> {
	await api.put(`ftl-quote/${request.quoteId}/update-shipment-details`, {
		method: "PUT",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(request),
		hooks: {
			beforeError: [
				async (error) => {
					const { response } = error;
					if (response) {
						const contentType = response.headers.get("content-type") ?? "";
						if (!contentType.toLowerCase().includes("application/json")) {
							return error;
						}

						try {
							const responseBody: unknown = await response.clone().json();
							const parsedError =
								updateShipmentDetailsErrorSchema.safeParse(responseBody);
							if (parsedError.success) {
								const messages = parsedError.data
									.map((e) => e.message)
									.join(" ");
								error.message = messages;
							}
						} catch {
							// Keep the original HTTP error so downstream status-based mapping still works.
							return error;
						}
					}
					return error;
				},
			],
		},
	});
}

type UseUpdateShipmentDetailsOptions = {
	onSuccess?: () => void;
	onError?: () => void;
};

export function useUpdateShipmentDetails(
	options?: UseUpdateShipmentDetailsOptions,
) {
	const { showSuccess, showError } = useNotifications();
	const consecutiveFailureCountRef = useRef(0);

	const { mutate, isPending, isSuccess, isError } = useMutation({
		mutationFn: callUpdateShipmentDetails,
		onSuccess: () => {
			consecutiveFailureCountRef.current = 0;

			// Handle successful update
			showSuccess(`FTL quote updated successfully!`);
			// Call custom onSuccess callback if provided
			if (options?.onSuccess) {
				options.onSuccess();
			}
		},
		onError: async (error) => {
			consecutiveFailureCountRef.current += 1;

			showError(
				await mapUpdateShipmentDetailsErrorToUserMessage(
					error,
					consecutiveFailureCountRef.current,
				),
			);
			options?.onError?.();
		},
	});

	return {
		updateShipmentDetails: mutate,
		isPending,
		isSuccess,
		isError,
	};
}
